import React, { useEffect } from 'react';

import { Link, useLocation } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { ROLE_GROUPS, ROLES } from '../_helpers/roles';
import { classSlug } from '../_helpers/normalize';
import { useImpersonation, getImpersonation } from './panel/impersonation';
import { useNotificationActions } from '_actions';
import { unreadNotificationsAtom } from '_state';

import { Layout } from 'antd';
import {
  HomeOutlined,
  InfoCircleOutlined,
  UserOutlined,
  LogoutOutlined,
  UploadOutlined,
  TeamOutlined,
  CalendarOutlined,
  DollarOutlined,
  BellOutlined,
  DashboardOutlined,
  TableOutlined,
  TrophyOutlined,
  FileTextOutlined,
  SwapOutlined,
} from '@ant-design/icons';

const { Sider } = Layout;

export { Nav };

// Unified sidebar: one column, grouped into the current-class section ("Lớp học")
// and the global management/menu section. Class switcher pinned on top, account
// actions pinned on the bottom. Replaces the old header/sidebar duplicated nav.
function Nav(props) {
  const location = useLocation();
  const auth = props.auth;
  const collapsed = props.collapsed !== undefined ? props.collapsed : false;
  const onCollapse = props.onCollapse || (() => {});
  const onChooseClass = props.onChooseClass || (() => {});
  const curClass = props.curClass;
  const notificationActions = useNotificationActions();
  const [unread, setUnread] = useRecoilState(unreadNotificationsAtom);

  const { stopImpersonate } = useImpersonation();
  const impersonating = getImpersonation();

  const userData = JSON.parse(localStorage.getItem('userData'));
  useEffect(() => {
    if (!auth || !userData) return;
    let alive = true;
    const loadUnread = () => notificationActions.getUnreadCount().then(n => { if (alive) setUnread(n); }).catch(() => {});
    loadUnread();
    const onFocus = () => loadUnread();
    const onVisible = () => { if (document.visibilityState === 'visible') loadUnread(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      alive = false;
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth, userData?.student_id]);
  if (!auth || !userData) return null;

  const role = userData.role;
  // admin is a superuser: full staff+manager nav, plus the admin-only /panel.
  const isAdmin = role === ROLES.ADMIN;
  const isStaff = ROLE_GROUPS.STAFF.includes(role);
  const isManager = ROLE_GROUPS.MANAGERS.includes(role);
  const isStudent = role === ROLES.STUDENT;
  const slug = curClass ? classSlug(curClass) : null;

  // Contextual tabs for the currently-selected class.
  const classTabs = !curClass ? [] : isStaff ? [
    { to: `/${slug}/dashboard`,        icon: <DashboardOutlined />,  label: 'Dashboard' },
    { to: `/${slug}/studentinfo`,      icon: <InfoCircleOutlined />, label: 'Thông tin học sinh' },
    { to: `/${slug}/studentscore`,     icon: <TableOutlined />,      label: 'Bảng điểm' },
    { to: `/${slug}/attendance`,       icon: <CalendarOutlined />,   label: 'Điểm danh' },
    { to: `/${slug}/home-assignments`, icon: <FileTextOutlined />,   label: 'Bài tập' },
  ] : isStudent ? [
    // Lớp học thử doesn't mark students, so a trial student's attendance page would be all blanks.
    ...(curClass.is_trial ? [] : [
      { to: `/${slug}/attendance`,     icon: <CalendarOutlined />,   label: 'Điểm danh' },
    ]),
    { to: `/${slug}/home-assignments`, icon: <FileTextOutlined />,   label: 'Bài tập' },
    { to: '/personalscore',            icon: <UserOutlined />,       label: 'Bảng điểm cá nhân' },
  ] : [];

  // Global (non-class) destinations, role-filtered.
  const globalItems = [
    { to: isStudent ? '/stuhome' : '/', icon: <HomeOutlined />, label: 'Trang chủ', show: true },
    { to: '/one-on-one',                icon: <TeamOutlined />, label: '1:1', show: true },
    { to: '/notifications',             icon: <BellOutlined />, label: 'Thông báo', show: true, badge: unread > 99 ? '99+' : unread },
    { to: '/manager',                   icon: <TeamOutlined />,     label: 'Quản lý lớp học', show: isManager || role === ROLES.TEACHER },
    { to: '/manager/students',          icon: <InfoCircleOutlined />, label: 'Quản lý học sinh', show: isStaff },
    { to: '/manager/staff',             icon: <TeamOutlined />,     label: 'Quản lý giáo viên', show: isManager },
    { to: '/schedule',                  icon: <CalendarOutlined />, label: 'Lịch dạy', show: isStaff },
    { to: '/manager/teachers',          icon: <TrophyOutlined />,   label: 'KPI giáo viên', show: isManager },
    { to: '/tuition',                   icon: <DollarOutlined />,   label: 'Học phí', show: isStaff },
    { to: '/dbportal',                  icon: <UploadOutlined />,   label: 'Quản lý CSDL', show: isStaff },
    { to: '/panel',                     icon: <DashboardOutlined />, label: 'Admin Panel', show: isAdmin },
  ].filter(i => i.show);

  const isActive = to => location.pathname === to;
  const Item = ({ to, icon, label, badge }) => (
    <Link to={to} className={'side-nav-item' + (isActive(to) ? ' active' : '')}>
      {icon}
      <span className="sni-label">{label}</span>
      {badge ? <span className="sni-badge">{badge}</span> : null}
    </Link>
  );

  const initials = (curClass?.class_name || 'Lớp')
    .replace(/[^A-Za-z0-9]/g, '').slice(0, 2).toUpperCase() || 'LỚP';

  return (
    <Sider
      className="app-sider"
      collapsible
      collapsed={collapsed}
      onCollapse={onCollapse}
      trigger={null}
      breakpoint="lg"
      collapsedWidth={0}
      width={232}
      onBreakpoint={broken => onCollapse(broken)}
    >
      <div className="side-inner">

        {/* ponytail: banner lives in the sider; on mobile the sider collapses to
            width 0, so if you get stuck impersonating there, just log out. */}
        {impersonating &&
          <div style={{ background: 'var(--bg-subtle)', border: '1px solid var(--line)', borderLeft: '3px solid var(--accent)', borderRadius: 8, padding: 10, margin: '0 8px 12px', fontSize: 13, color: 'var(--ink)' }}>
            <div style={{ marginBottom: 6 }}>👤 Đang xem với tư cách <b>{impersonating.target}</b></div>
            <button type="button" className="class-switch" style={{ width: '100%', justifyContent: 'center' }} onClick={stopImpersonate}>
              ↩ Quay lại admin
            </button>
          </div>}

        {isStaff &&
          <button type="button" className="class-switch" onClick={onChooseClass}>
            <span className="cs-badge">{initials}</span>
            <span className="cs-text">
              <span className="cs-name">{curClass?.class_name || 'Chọn lớp'}</span>
              <span className="cs-sub">Đổi lớp</span>
            </span>
            <SwapOutlined className="cs-icon" />
          </button>}

        {classTabs.length > 0 &&
          <>
            <div className="side-group-label">Lớp học</div>
            {classTabs.map(t => <Item key={t.to} {...t} />)}
          </>}

        <div className="side-group-label">{isStaff ? 'Quản lý' : 'Menu'}</div>
        {globalItems.map(t => <Item key={t.to} {...t} />)}

        <div className="side-spacer" />
      </div>
    </Sider>
  );
}
